import { value } from '@takeshape/util';
import { updateAuthentication } from "../util/authentication.js";
import { isServiceConfig } from "../util/config.js";
import { hasEveryKey } from "../util/has.js";
import { getHealthCheck } from "../util/health.js";
export const name = 'googleVertex';
export const provider = {
    name,
    title: 'Google Vertex',
    description: 'Train high-quality custom machine learning models with minimal machine learning expertise and effort.',
    categories: ['ai'],
    links: [
        { title: 'Homepage', url: 'https://cloud.google.com/vertex-ai' },
        { title: 'Docs', url: '/docs/services/providers/google-vertex' }
    ],
    serviceType: 'rest',
    createId: value(name),
    getFormSchema: value({
        type: 'object',
        required: ['title', 'id', 'project', 'location', 'authentication'],
        properties: {
            title: {
                title: 'Name',
                type: 'string',
                default: 'Google Vertex'
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+',
                default: 'google-vertex'
            },
            project: {
                title: 'Project',
                type: 'string'
            },
            location: {
                title: 'Location',
                type: 'string'
            },
            authentication: {
                title: 'Service Account Key (JSON)',
                description: `Everything uploaded will be encrypted with your project's data key.`,
                type: 'object',
                properties: {
                    client_email: {
                        title: 'Client Email',
                        type: 'string',
                        '@sensitive': true
                    },
                    private_key: {
                        title: 'Private Key',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['client_email', 'private_key']
            }
        }
    }),
    getFormConfig: value({
        order: ['title', 'id', 'project', 'location', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'The human-readable unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            project: {
                widget: 'singleLineText',
                instructions: 'The name of the Google Cloud project hosting your models.'
            },
            location: {
                widget: 'singleLineText',
                instructions: 'A location, or region, for where your models are hosted.'
            },
            authentication: {
                widget: 'jsonFile',
                instructions: 'Upload your service account key.'
            }
        }
    }),
    getConfig(formData) {
        const config = {
            id: formData.id,
            title: formData.title,
            namespace: formData.namespace,
            provider: name,
            serviceType: 'rest',
            options: {
                project: formData.project,
                location: formData.location
            },
            authenticationType: 'google'
        };
        return updateAuthentication(config, {
            authentication: formData.authentication
                ? {
                    clientEmail: formData.authentication.client_email,
                    privateKey: formData.authentication.private_key
                }
                : undefined
        }, { type: 'google' });
    },
    isCompleteConfig: hasEveryKey(['authentication', 'options.project', 'options.location']),
    isServiceConfig: isServiceConfig(name, [
        'authentication.clientEmail',
        'authentication.privateKey',
        'options.project',
        'options.location'
    ]),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint(serviceConfig) {
            if (!serviceConfig.options?.location || !serviceConfig.options.project) {
                throw new Error('Incomplete service config - missing "location" or "project"');
            }
            return `https://${serviceConfig.options.location}-aiplatform.googleapis.com/v1/projects/${serviceConfig.options.project}/locations/${serviceConfig.options.location}/models`;
        },
        requestMethod: 'GET',
        requestBodyFormat: 'json',
        healthyResponseStatusCode: 200,
        triggers: ['manual', 'schedule']
    }),
    getAiModels() {
        return {
            generate: [
                'gemini-1.5-pro-002',
                'gemini-1.5-flash-002',
                'gemini-1.5-pro-001',
                'gemini-1.5-flash-001',
                'gemini-1.0-pro-001',
                'gemini-1.0-pro-002',
                'gemini-1.0-pro-vision-001',
                'gemini-flash-experimental',
                'gemini-pro-experimental'
            ],
            embedding: [
                'text-embedding-004',
                'text-embedding-005',
                'textembedding-gecko@001',
                'textembedding-gecko@003',
                'textembedding-gecko-multilingual@001',
                'text-multilingual-embedding-002'
            ],
            image: []
        };
    }
};
